import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import axiosInstance from "../../../utils/axiosInstance";
import { useFormContext } from 'react-hook-form';
import AddressTable from "../AddressTable";
import Select from 'react-select';
import TimeslotDropdown from "../TimeslotDropdown";
import { TimezoneOptions } from "../../../tools/date.util";
import TimeslotLimitsTable from "../TimeslotLimitsTable";
import { showToast } from "../../toasts";

const SCALED_CHECKBOX_STYLE = {
    transform: 'scale(3)',
    width: '15px',
    height: '15px',
    padding: '0',
    margin: '0',
    marginLeft: '15px',
    marginTop: '10px'
};

const Bookings = ({
    selectedCompany,
    fetchCompany,
    companyAddresses,
    googleMapsApiKey,
    timeslots,
    user
}) =>{

    const {
        register,
        watch,
        setValue,
        reset,
        formState: { isDirty }
    } = useFormContext();

    const [isLoading, setIsLoading] = useState(false);
    const [adHocPermissions, setAdHocPermissions] = useState(null); // State for fetched permissions
    const [isFetchingPermissions, setIsFetchingPermissions] = useState(false); // Loading state for fetch
    const [whatsappButtonPermissions, setWhatsappButtonPermissions] = useState(null); // State for fetched WhatsApp button permissions
    const [isFetchingWhatsappPermissions, setIsFetchingWhatsappPermissions] = useState(false); // Loading state for WhatsApp fetch
    // Customer-app self-service booking (CU-124k0dxw6dv): the company switch and its roles.
    // Admin-only, fetched and saved on its own endpoint so the domain stays pluggable.
    const isAdmin = user?.role === 'admin';
    const [customerAppBookingSetting, setCustomerAppBookingSetting] = useState(null);
    const [isFetchingCustomerAppBooking, setIsFetchingCustomerAppBooking] = useState(false);
    const [customerAppBookingEnabled, setCustomerAppBookingEnabled] = useState(false);
    const [selectedCustomerAppBookingRoles, setSelectedCustomerAppBookingRoles] = useState([]);
    const [timeSlotRows, setTimeSlotRows] = useState([]);

    useEffect(() => {
        register('cancellation_range');
        register('booking_time_range');
        register('csb_role');
        register('opening_time');
        register('cut_off_time');
        register('ad_hoc_permissions'); // Register ad-hoc permissions as a single field
        register('whatsapp_button_permissions'); // Register WhatsApp button permissions as a single field
        register('customer_app_booking'); // Register the customer-app booking setting as a single field
        register('timezone');
        register('timeslot_limits');
        register('allow_inbound_bookings');
        register('allow_outbound_bookings');
        register('show_cancellation_actor');
    }, [register]);

    const [
        cancellation_range,
        booking_time_range,
        csb_role,
        opening_time,
        cut_off_time,
        ad_hoc_permissions,
        timezone,
        timeslot_limits,
        allow_inbound_bookings,
        allow_outbound_bookings,
        show_cancellation_actor
    ] = watch([
        'cancellation_range',
        'booking_time_range',
        'csb_role',
        'opening_time',
        'cut_off_time',
        'ad_hoc_permissions',
        'timezone',
        'timeslot_limits',
        'allow_inbound_bookings',
        'allow_outbound_bookings',
        'show_cancellation_actor'
    ]);

    const csb_roles = [
        { label: "Manager", value: "manager"},
        { label: "Employee", value: "employee"},
        { label: "HR Manager", value: "hr_manager" },
        { label: "Company Admin", value: "company_admin" },
        { label: "Logistics Specialist", value: "logistics_specialist" },
        { label: "Client Manager", value: "client_manager" },
    ];
    
    // Define dedicated array for ad-hoc permissions
    const adHocRoles = [
        { label: "Manager", value: "manager"},
        { label: "Employee", value: "employee"},
        { label: "Company Admin", value: "company_admin" },
        { label: "Logistics Specialist", value: "logistics_specialist" },
        { label: "Client Manager", value: "client_manager" },
    ];

    // Define dedicated array for Team Manager WhatsApp button permissions.
    // admin, client_manager and logistics_specialist always see the button, so
    // they are not selectable here.
    const whatsappButtonRoles = [
        { label: "Manager", value: "manager"},
        { label: "Employee", value: "employee"},
        { label: "HR Manager", value: "hr_manager" },
        { label: "Company Admin", value: "company_admin" },
    ];

    // Column per selectable role on WhatsappButtonPermission
    const whatsappButtonRoleColumns = {
        manager: 'manager_whatsapp_button_permission',
        employee: 'employee_whatsapp_button_permission',
        hr_manager: 'hr_manager_whatsapp_button_permission',
        company_admin: 'company_admin_whatsapp_button_permission'
    };

    // Previously saved roles
    const prevSavedRoles = () => {                
        let csbRolesDuplicate = {...selectedCompany.csb_role};
        ["id", "company_id", "created_at", "updated_at"].forEach(key => delete csbRolesDuplicate[key]);

        return Object.entries(csbRolesDuplicate)
            .filter(([_, value]) => value === true )
            .map(([key,_]) => {
                return csb_roles.find(csb_role => csb_role.value === key)
            });
    };
    
    const [selectedCsbRoles, setSelectedCsbRoles] = useState(prevSavedRoles()); // Selected roles
    const [selectedAdHocRoles, setSelectedAdHocRoles] = useState([]); // Selected ad-hoc permission roles
    const [selectedWhatsappButtonRoles, setSelectedWhatsappButtonRoles] = useState([]); // Selected WhatsApp button permission roles

    // Format CSB role data
    const formatSelectedCsbRoles = () => {
        const roleData = {};
        csb_roles.forEach((role) => {
          roleData[role.value] = selectedCsbRoles.some((selectedRole) => selectedRole?.value === role.value);
        });
        return roleData;
    };
    
    // Format ad-hoc permissions data
    const formatAdHocPermissions = () => {
        const permissionsData = {
            manager_ad_hoc_permission: false,
            employee_ad_hoc_permission: false,
            company_admin_ad_hoc_permission: false,
            logistic_specialist_ad_hoc_permission: false,
            client_manager_ad_hoc_permission: false
        };

        selectedAdHocRoles.forEach(role => {
            switch(role.value) {
                case 'manager':
                    permissionsData.manager_ad_hoc_permission = true;
                    break;
                case 'employee':
                    permissionsData.employee_ad_hoc_permission = true;
                    break;
                case 'company_admin':
                    permissionsData.company_admin_ad_hoc_permission = true;
                    break;
                case 'logistics_specialist':
                    permissionsData.logistic_specialist_ad_hoc_permission = true;
                    break;
                case 'client_manager':
                    permissionsData.client_manager_ad_hoc_permission = true;
                    break;
                default:
                    break;
            }
        });
        
        return permissionsData;
    };
    
    // Format Team Manager WhatsApp button permissions data.
    // Nothing selected means the button is disabled for every gated role.
    const formatWhatsappButtonPermissions = (roles = selectedWhatsappButtonRoles) => {
        const permissionsData = {};

        Object.values(whatsappButtonRoleColumns).forEach(column => {
            permissionsData[column] = false;
        });

        roles.forEach(role => {
            permissionsData[whatsappButtonRoleColumns[role.value]] = true;
        });

        return permissionsData;
    };

    const handleCsbRoleChange = (selectedOptions) => {
        setSelectedCsbRoles(selectedOptions || []);
        setValue('csb_role', formatSelectedCsbRoles(), { shouldDirty: true });
    };
    
    const handleAdHocPermissionChange = (selectedOptions) => {
        setSelectedAdHocRoles(selectedOptions || []);
        setValue('ad_hoc_permissions', formatAdHocPermissions(), { shouldDirty: true });
    };
    
    const handleWhatsappButtonPermissionChange = (selectedOptions) => {
        const roles = selectedOptions || [];

        setSelectedWhatsappButtonRoles(roles);
        setValue('whatsapp_button_permissions', formatWhatsappButtonPermissions(roles), { shouldDirty: true });
    };

    const handleOpeningTimeChange = (selectedTime) => {
        setValue('opening_time', selectedTime, { shouldDirty: true });
    };

    const handleCutOffTimeChange = (selectedTime) => {
        setValue('cut_off_time', selectedTime, { shouldDirty: true });
    };

    const handleTimezoneChange = (selectedOption) => {
        setValue('timezone', selectedOption.value, { shouldDirty: true });
    };

    const handleTimeslotSettingsChange = (newRows) => {
        setTimeSlotRows(newRows);
        // Mark form as dirty when timeslot rows change so save button becomes enabled
        // We trigger a change on a watched field to mark the form as dirty
        setValue('timeslot_limits', timeslot_limits !== undefined ? timeslot_limits : true, { shouldDirty: true });
    };

    // Effect to fetch AdHocPermissions when selectedCompany.id changes
    useEffect(() => {
        const fetchAdHocPermissions = async () => {
            if (!selectedCompany?.id) return; // Don't fetch if no company ID

            setIsFetchingPermissions(true);
            try {
                const response = await axiosInstance.get(`/companies/${selectedCompany.id}/ad_hoc_permission.json`);
                setAdHocPermissions(response.data);
            } catch (error) {
                console.error("Failed to fetch ad-hoc permissions:", error);
                setAdHocPermissions(null); // Reset or handle error state
            } finally {
                setIsFetchingPermissions(false);
            }
        };

        fetchAdHocPermissions();
    }, [selectedCompany?.id]); // Dependency on company ID

    // Defined outside the effect and memoised on the company id, so the effect re-runs
    // when the selected company changes rather than on every render
    const fetchWhatsappButtonPermissions = useCallback(async () => {
        if (!selectedCompany?.id) return; // Don't fetch if no company ID

        setIsFetchingWhatsappPermissions(true);
        try {
            const response = await axiosInstance.get(`/companies/${selectedCompany.id}/whatsapp_button_permission.json`);
            // A denied request redirects to an HTML page that axios resolves as a 200, so
            // reject anything that isn't the permission object rather than rendering an empty selector
            setWhatsappButtonPermissions(response.data && typeof response.data === 'object' ? response.data : null);
        } catch (error) {
            console.error("Failed to fetch WhatsApp button permissions:", error);
            setWhatsappButtonPermissions(null); // Reset or handle error state
        } finally {
            setIsFetchingWhatsappPermissions(false);
        }
    }, [selectedCompany?.id]); // Dependency on company ID

    // Effect to fetch WhatsappButtonPermissions when selectedCompany.id changes
    useEffect(() => {
        fetchWhatsappButtonPermissions();
    }, [fetchWhatsappButtonPermissions]);

    // Roles a company may allow to book from the customer app (CustomerAppBookingSetting::ROLES)
    const customerAppBookingRoles = [
        { label: 'Employee', value: 'employee' },
        { label: 'Manager', value: 'manager' },
        { label: 'HR Manager', value: 'hr_manager' },
        { label: 'Company Admin', value: 'company_admin' },
        { label: 'Logistics Specialist', value: 'logistics_specialist' },
        { label: 'Client Manager', value: 'client_manager' },
        { label: 'Site Manager', value: 'site_manager' },
    ];

    const fetchCustomerAppBookingSetting = useCallback(async () => {
        if (!selectedCompany?.id || !isAdmin) return; // The endpoint is admin-only; others never see the block

        setIsFetchingCustomerAppBooking(true);
        try {
            const response = await axiosInstance.get(`/companies/${selectedCompany.id}/customer_app_booking_setting.json`);
            setCustomerAppBookingSetting(response.data && typeof response.data === 'object' ? response.data : null);
        } catch (error) {
            console.error('Failed to fetch customer app booking setting:', error);
            setCustomerAppBookingSetting(null);
        } finally {
            setIsFetchingCustomerAppBooking(false);
        }
    }, [selectedCompany?.id, isAdmin]);

    useEffect(() => {
        fetchCustomerAppBookingSetting();
    }, [fetchCustomerAppBookingSetting]);

    // Seed the block from the fetched setting (no row reads back as disabled with no roles)
    useEffect(() => {
        if (customerAppBookingSetting) {
            const savedRoles = (customerAppBookingSetting.allowed_roles || []).
                map(roleValue => customerAppBookingRoles.find(role => role.value === roleValue)).
                filter(Boolean);
            setCustomerAppBookingEnabled(Boolean(customerAppBookingSetting.enabled));
            setSelectedCustomerAppBookingRoles(savedRoles);
            setValue('customer_app_booking', { enabled: Boolean(customerAppBookingSetting.enabled), allowed_roles: savedRoles.map(role => role.value) });
        }
    }, [customerAppBookingSetting]);

    const handleCustomerAppBookingEnabledChange = (event) => {
        const enabled = event.target.checked;
        setCustomerAppBookingEnabled(enabled);
        setValue('customer_app_booking', { enabled, allowed_roles: selectedCustomerAppBookingRoles.map(role => role.value) }, { shouldDirty: true });
    };

    const handleCustomerAppBookingRolesChange = (selectedOptions) => {
        const roles = selectedOptions || [];
        setSelectedCustomerAppBookingRoles(roles);
        setValue('customer_app_booking', { enabled: customerAppBookingEnabled, allowed_roles: roles.map(role => role.value) }, { shouldDirty: true });
    };

    // Fetch active timeslots on page load
    useEffect(() => {
        const activeSlots = timeslots.filter(slot => 
            slot.company_admin_permission || slot.manager_permission || slot.employee_permission
        );
        setTimeSlotRows(activeSlots);
    }, [timeslots]);

    const whatsappSelectionFromPermissions = (permissions) => {
        return Object.keys(whatsappButtonRoleColumns).
            filter(roleValue => permissions[whatsappButtonRoleColumns[roleValue]]).
            map(roleValue => whatsappButtonRoles.find(role => role.value === roleValue)).
            filter(Boolean);
    };

    // Transform ad-hoc permissions to selected options format when data is fetched
    useEffect(() => {
        if (adHocPermissions) {
            const selectedPermissions = [];
            
            if (adHocPermissions.manager_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'manager'));
            }
            if (adHocPermissions.employee_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'employee'));
            }
            if (adHocPermissions.company_admin_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'company_admin'));
            }
            if (adHocPermissions.logistic_specialist_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'logistics_specialist'));
            }
            if (adHocPermissions.client_manager_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'client_manager'));
            }

            setSelectedAdHocRoles(selectedPermissions);
            setValue('ad_hoc_permissions', formatAdHocPermissions());
        }
    }, [adHocPermissions]);

    // Transform WhatsApp button permissions to selected options format when data is fetched
    useEffect(() => {
        if (whatsappButtonPermissions) {
            const savedRoles = whatsappSelectionFromPermissions(whatsappButtonPermissions);

            setSelectedWhatsappButtonRoles(savedRoles);
            setValue('whatsapp_button_permissions', formatWhatsappButtonPermissions(savedRoles));
        }
    }, [whatsappButtonPermissions]);

    // Save changes
    const updateCompany = async () => {
        // Validate that at least one trip type is enabled
        const inboundAllowed = allow_inbound_bookings ?? true;
        const outboundAllowed = allow_outbound_bookings ?? true;
        
        if (!inboundAllowed && !outboundAllowed) {
            showToast('error', 'At least one trip type (inbound or outbound) must be allowed');
            return;
        }
        
        setIsLoading(true);
        try {
            // Update core company details
            const response = await axiosInstance(`/companies/${selectedCompany.id}.json`, {
                method: 'PUT',
                data: { 
                    company: {
                        cancellation_range,
                        booking_time_range,
                        opening_time,
                        cut_off_time,
                        timezone,
                        timeslot_limits: timeslot_limits,
                        allow_inbound_bookings: inboundAllowed,
                        allow_outbound_bookings: outboundAllowed,
                        show_cancellation_actor,
                    }
                }
            });

            // Update CSB roles
            await axiosInstance(`/companies/${selectedCompany.id}/csb_roles/${selectedCompany.csb_role.id}.json`, {
                method: 'PUT',
                data: { 
                    csb_role: formatSelectedCsbRoles(),
                },
                headers: { 
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
            });

            // Update Ad-Hoc Permissions
            if (adHocPermissions?.id) {
                const adHocPermissionsData = formatAdHocPermissions();
                const response = await axiosInstance.put(
                    `/companies/${selectedCompany.id}/ad_hoc_permission.json`,
                    { 
                        ad_hoc_permission: adHocPermissionsData
                    },
                    {
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        }
                    }
                );
                setAdHocPermissions(response.data);
            } else {
                console.warn("AdHocPermission data not available or missing ID, skipping update.", adHocPermissions);
            }

            // Update Team Manager WhatsApp Button Permissions.
            // Checked on the row rather than its id: a company whose row was removed reads back
            // an unsaved record with a null id, and the PUT is what creates it again.
            if (whatsappButtonPermissions) {
                const whatsappButtonPermissionsData = formatWhatsappButtonPermissions();
                const whatsappResponse = await axiosInstance.put(
                    `/companies/${selectedCompany.id}/whatsapp_button_permission.json`,
                    {
                        whatsapp_button_permission: whatsappButtonPermissionsData
                    },
                    {
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        }
                    }
                );
                setWhatsappButtonPermissions(whatsappResponse.data);
            } else {
                console.warn("WhatsappButtonPermission data not available, skipping update.", whatsappButtonPermissions);
            }

            // Update the customer-app booking setting (admin only). The PUT creates the row on first save.
            if (isAdmin && customerAppBookingSetting) {
                const customerAppResponse = await axiosInstance.put(
                    `/companies/${selectedCompany.id}/customer_app_booking_setting.json`,
                    {
                        customer_app_booking_setting: {
                            enabled: customerAppBookingEnabled,
                            allowed_roles: selectedCustomerAppBookingRoles.map(role => role.value)
                        }
                    },
                    {
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        }
                    }
                );
                setCustomerAppBookingSetting(customerAppResponse.data);
            }

            // If timeslot_limits is true, update the timeslot settings          
            if (timeslot_limits && timeSlotRows && timeSlotRows.length > 0) {
                for (const slot of timeSlotRows) {
                    // Ensure we have required fields
                    if (!slot.timeslot || !slot.trip_type) {
                        console.error('Missing required fields for timeslot setting:', slot);
                        continue;
                    }
                    
                    const payload = {
                        timeslot: slot.timeslot,
                        arrival_time_buffer_in_minutes: slot.arrival_time_buffer_in_minutes ?? 10,
                        traffic_buffer_in_minutes: slot.traffic_buffer_in_minutes ?? 0,
                        company_admin_permission: slot.company_admin_permission ?? false,
                        manager_permission: slot.manager_permission ?? false,
                        employee_permission: slot.employee_permission ?? false,
                        active_timeslot: slot.active_timeslot ?? false,
                        trip_type: slot.trip_type
                    };

                    if (slot.id) {
                        // Update existing timeslot
                        await axiosInstance(`/companies/${selectedCompany.id}/timeslot_settings/${slot.id}.json`, {
                            method: 'PUT',
                            data: { timeslot_setting: payload },
                            headers: { 
                                Accept: 'application/json',
                                'Content-Type': 'application/json'
                            }
                        });
                    } else {
                        // Find existing timeslot setting by timeslot and trip_type
                        // Since timeslot_settings are created by default for all timeslots, we should find and update
                        try {
                            // Fetch all timeslot settings to find the matching one
                            const existingSettingsResponse = await axiosInstance.get(
                                `/companies/${selectedCompany.id}/timeslot_settings.json`
                            );
                            const existingSettings = existingSettingsResponse.data || [];
                            
                            // Find matching setting - compare both timeslot and trip_type
                            // timeslot might be stored as integer enum, so we need to handle both string and number comparisons
                            const existingSetting = existingSettings.find(s => {
                                const timeslotMatch = s.timeslot === slot.timeslot || 
                                                     String(s.timeslot) === String(slot.timeslot);
                                const tripTypeMatch = s.trip_type === slot.trip_type || 
                                                     (s.trip_type === null && slot.trip_type === null);
                                return timeslotMatch && tripTypeMatch;
                            });
                            
                            if (existingSetting && existingSetting.id) {
                                // Update existing
                                await axiosInstance(`/companies/${selectedCompany.id}/timeslot_settings/${existingSetting.id}.json`, {
                                    method: 'PUT',
                                    data: { timeslot_setting: payload },
                                    headers: { 
                                        Accept: 'application/json',
                                        'Content-Type': 'application/json'
                                    }
                                });
                            } else {
                                // Create new if it doesn't exist (shouldn't happen normally, but handle gracefully)
                                await axiosInstance(`/companies/${selectedCompany.id}/timeslot_settings.json`, {
                                    method: 'POST',
                                    data: { timeslot_setting: payload },
                                    headers: { 
                                        Accept: 'application/json',
                                        'Content-Type': 'application/json'
                                    }
                                });
                            }
                        } catch (error) {
                            console.error('Error saving timeslot setting:', error);
                            showToast('error', `Failed to save timeslot setting: ${error.response?.data?.error || error.message}`);
                            throw error;
                        }
                    }
                }
            }

            // Fetch fresh company data to ensure overall consistency
            await fetchCompany();
            
            // Refresh timeslot settings to get updated data with IDs
            if (timeslot_limits) {
                try {
                    const timeslotsResponse = await axiosInstance.get(
                        `/companies/${selectedCompany.id}/timeslot_settings.json`
                    );
                    const updatedTimeslots = timeslotsResponse.data || [];
                    
                    // Update timeSlotRows with fresh data that includes IDs
                    const updatedRows = timeSlotRows.map(row => {
                        // Find matching timeslot setting from the refreshed data
                        const matchingSetting = updatedTimeslots.find(s => {
                            const timeslotMatch = s.timeslot === row.timeslot || 
                                                 String(s.timeslot) === String(row.timeslot);
                            const tripTypeMatch = s.trip_type === row.trip_type || 
                                                 (s.trip_type === null && row.trip_type === null);
                            return timeslotMatch && tripTypeMatch;
                        });
                        
                        // If we found a match, use its ID and merge with our row data
                        if (matchingSetting) {
                            return {
                                ...row,
                                id: matchingSetting.id,
                                ...matchingSetting
                            };
                        }
                        return row;
                    });
                    
                    setTimeSlotRows(updatedRows);
                } catch (error) {
                    console.error('Error refreshing timeslot settings:', error);
                }
            }

            // Reset the form state with the latest saved values to clear the dirty state
            reset({
                cancellation_range,
                booking_time_range,
                opening_time,
                cut_off_time,
                ad_hoc_permissions: formatAdHocPermissions(),
                whatsapp_button_permissions: formatWhatsappButtonPermissions(),
                timeslot_limits,
                show_cancellation_actor,
            });

            showToast('success', 'Company settings updated successfully');

        } catch (error) {
            console.error("Couldn't update company settings: ", error);
            
            // Handle validation errors from backend
            if (error.response?.status === 422) {
                const errorData = error.response.data;
                let errorMessage = 'Failed to update company settings';
                
                // Check for validation errors
                if (errorData.errors) {
                    if (errorData.errors.base && errorData.errors.base.length > 0) {
                        errorMessage = errorData.errors.base[0];
                    } else if (errorData.errors.allow_inbound_bookings || errorData.errors.allow_outbound_bookings) {
                        errorMessage = 'At least one trip type (inbound or outbound) must be allowed';
                    } else {
                        // Get first error message
                        const firstError = Object.values(errorData.errors)[0];
                        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
                    }
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
                
                showToast('error', errorMessage);
            } else {
                showToast('error', 'Failed to update company settings. Please try again.');
            }
        } 
        finally {
            setIsLoading(false);
        }
    };

    // Revert unsaved changes
    const handleRevertChanges = async () => {
        const originalRoles = prevSavedRoles();
        setSelectedCsbRoles(originalRoles);
        
        // Reset ad-hoc permissions based on fetched data
        if (adHocPermissions) {
            const selectedPermissions = [];
            
            if (adHocPermissions.manager_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'manager'));
            }
            if (adHocPermissions.employee_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'employee'));
            }
            if (adHocPermissions.company_admin_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'company_admin'));
            }
            if (adHocPermissions.logistic_specialist_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'logistics_specialist'));
            }
            if (adHocPermissions.client_manager_ad_hoc_permission) {
                selectedPermissions.push(adHocRoles.find(role => role.value === 'client_manager'));
            }

            setSelectedAdHocRoles(selectedPermissions);
        }

        // Reset WhatsApp button permissions based on fetched data
        const savedWhatsappButtonRoles = whatsappButtonPermissions ? whatsappSelectionFromPermissions(whatsappButtonPermissions) : [];

        setSelectedWhatsappButtonRoles(savedWhatsappButtonRoles);
        
        // Reset timeslot settings to original state
        const activeSlots = timeslots.filter(slot => 
            slot.company_admin_permission || slot.manager_permission || slot.employee_permission
        );
        setTimeSlotRows(activeSlots);
        
        reset({
            ad_hoc_permissions: formatAdHocPermissions(),
            whatsapp_button_permissions: formatWhatsappButtonPermissions(savedWhatsappButtonRoles),
            cancellation_range: selectedCompany.cancellation_range,
            booking_time_range: selectedCompany.booking_time_range,
            opening_time: selectedCompany.opening_time,
            cut_off_time: selectedCompany.cut_off_time,
            timeslot_limits: selectedCompany.timeslot_limits,
            allow_inbound_bookings: selectedCompany.allow_inbound_bookings ?? true,
            allow_outbound_bookings: selectedCompany.allow_outbound_bookings ?? true,
            show_cancellation_actor: selectedCompany.show_cancellation_actor ?? false
        });
    };

    // Initialize form state with company data when available
    useEffect(() => {
        if (selectedCompany) {
            setValue('cancellation_range', selectedCompany.cancellation_range);
            setValue('booking_time_range', selectedCompany.booking_time_range);
            setValue('opening_time', selectedCompany.opening_time);
            setValue('cut_off_time', selectedCompany.cut_off_time);
            setValue('timeslot_limits', selectedCompany.timeslot_limits);
            setValue('allow_inbound_bookings', selectedCompany.allow_inbound_bookings ?? true);
            setValue('allow_outbound_bookings', selectedCompany.allow_outbound_bookings ?? true);
            setValue('show_cancellation_actor', selectedCompany.show_cancellation_actor ?? false);

            if (selectedCompany.csb_role) { 
                setSelectedCsbRoles(prevSavedRoles());
            }
        }
    }, [selectedCompany, setValue]);
    
    return (
        <div className="w-full p-4 flex flex-col">

            {/* Limitations section */}
            <div className="limitations flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Limitations</h3>
                        <p className="text-sm text-gray-500">Time limits for bookings</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col">

                    <div className="w-full flex flex-wrap">

                        <div className="w-full md:w-1/2 flex flex-col pr-1">
                            <label className="label font-medium text-gray-900">Cancellation Period</label>
                            <input
                                type="text"
                                value={cancellation_range}
                                onChange={(e) => setValue('cancellation_range', e.target.value, { shouldDirty: true })}
                                className="input border text-sm py-1 rounded-lg w-full border-gray-400 text-gray-900 px-2"
                            />
                        </div>

                        <div className="w-full md:w-1/2 flex flex-col pl-1">
                            <label className="label font-medium text-gray-900">Booking Time Range</label>
                            <input
                                type="text"
                                value={booking_time_range}
                                onChange={(e) => setValue('booking_time_range', e.target.value, { shouldDirty: true })}
                                className="input border text-sm py-1 rounded-lg w-full border-gray-400 text-gray-900 px-2"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-5" />

            {/* Team Manager section */}
            <div className="team_manager flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Team Manager</h3>
                        <p className="text-sm text-gray-500">Team manager accessibility</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col space-y-4">

                    <div className="w-full flex flex-col mb-2">
                        <label className="label font-medium text-gray-900">Permitted Roles</label>
                        <Select
                            placeholder={'Multi Select Roles'}
                            isMulti
                            isSearchable
                            options={csb_roles}
                            onChange={handleCsbRoleChange}
                            closeMenuOnSelect={false}
                            value={selectedCsbRoles}
                        />
                    </div>

                    <div className="w-full flex flex-col mb-2">
                        <label className="label font-medium text-gray-900">Ad-hoc Booking Capabilities</label>
                        <Select
                            placeholder={'Select roles with ad-hoc booking permissions'}
                            isMulti
                            isSearchable
                            options={adHocRoles}
                            onChange={handleAdHocPermissionChange}
                            closeMenuOnSelect={false}
                            value={selectedAdHocRoles}
                            isLoading={isFetchingPermissions}
                        />
                        {isFetchingPermissions && <p className="text-xs text-gray-400 mt-1">Loading permissions...</p>}
                    </div>

                    <div className="w-full flex flex-col mb-2" data-testid="whatsapp-button-roles-select">
                        <label className="label font-medium text-gray-900">Team Manager WhatsApp Button</label>
                        <Select
                            placeholder={'No roles selected — button disabled'}
                            isMulti
                            isSearchable
                            options={whatsappButtonRoles}
                            onChange={handleWhatsappButtonPermissionChange}
                            closeMenuOnSelect={false}
                            value={selectedWhatsappButtonRoles}
                            isLoading={isFetchingWhatsappPermissions}
                        />
                        {isFetchingWhatsappPermissions && <p className="text-xs text-gray-400 mt-1">Loading permissions...</p>}
                    </div>

                    {isAdmin && (
                        <div className="w-full flex flex-col mb-2" data-testid="customer-app-booking-block">
                            <p className="label font-medium text-gray-900">Customer App Booking</p>
                            <p className="text-sm text-gray-500 mb-2">Lets passengers book and edit their own trips from the customer app. The Bookings entry only appears in the app for the roles selected here.</p>
                            <label className="inline-flex items-center mb-2">
                                <input
                                    type="checkbox"
                                    className="form-checkbox h-5 w-5 text-purple-600"
                                    data-testid="customer-app-booking-enabled"
                                    checked={customerAppBookingEnabled}
                                    onChange={handleCustomerAppBookingEnabledChange}
                                    disabled={isFetchingCustomerAppBooking}
                                />
                                <span className="ml-2 text-sm text-gray-700">Enable in-app booking for this company</span>
                            </label>
                            <div data-testid="customer-app-booking-roles">
                                <Select
                                    placeholder={'Select roles allowed to book in the app'}
                                    isMulti
                                    isSearchable
                                    options={customerAppBookingRoles}
                                    onChange={handleCustomerAppBookingRolesChange}
                                    closeMenuOnSelect={false}
                                    value={selectedCustomerAppBookingRoles}
                                    isLoading={isFetchingCustomerAppBooking}
                                    isDisabled={!customerAppBookingEnabled}
                                />
                            </div>
                            {customerAppBookingEnabled && selectedCustomerAppBookingRoles.length === 0 && (
                                <p className="text-xs text-orange-600 mt-1">Enabled with no roles selected: nobody can book until a role is added.</p>
                            )}
                        </div>
                    )}

                    <div className="w-full flex flex-wrap">

                        <div className="w-full md:w-1/2 flex flex-col pr-2">
                            <label className="label font-medium text-gray-900">Booking Opening Time</label>
                            <TimeslotDropdown 
                                selectedCompany={selectedCompany}
                                value={opening_time}
                                onChange={handleOpeningTimeChange}
                                timeslots={timeslots}
                            />
                        </div>

                        <div className="w-full md:w-1/2 flex flex-col">
                            <label className="label font-medium text-gray-900">Booking Closing Time</label>
                            <TimeslotDropdown 
                                selectedCompany={selectedCompany}
                                value={cut_off_time}
                                onChange={handleCutOffTimeChange}
                                timeslots={timeslots}
                            />
                        </div>

                        <div className="w-full flex flex-wrap mt-4">
                            <div className="w-full md:w-1/2 flex flex-col pr-2">
                                <label className="label font-medium text-gray-900">Timezone</label>
                                <Select
                                    value={TimezoneOptions.find(option => option.value === timezone)}
                                    onChange={handleTimezoneChange}
                                    options={TimezoneOptions}
                                    className="basic-single"
                                    classNamePrefix="select"
                                    isSearchable={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-5" />

            {/* Trip Type Settings section */}
            <div className="trip_type_settings flex flex-wrap px-3 py-4 md:px-0">
                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Trip Type Settings</h3>
                        <p className="text-sm text-gray-500">Control which trip types are allowed for bookings</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4">
                    <div className="w-full flex space-x-4">
                        <div className="w-full flex flex-col">
                            <label className="label font-medium text-gray-900">Allow Inbound Bookings</label>
                            <label className="text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                                    checked={allow_inbound_bookings ?? true}
                                    onChange={e => {
                                        setValue('allow_inbound_bookings', e.target.checked, { shouldDirty: true });
                                    }}
                                    style={SCALED_CHECKBOX_STYLE}
                                />
                            </label>
                        </div>
                        <div className="w-full flex flex-col">
                            <label className="label font-medium text-gray-900">Allow Outbound Bookings</label>
                            <label className="text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                                    checked={allow_outbound_bookings ?? true}
                                    onChange={e => {
                                        setValue('allow_outbound_bookings', e.target.checked, { shouldDirty: true });
                                    }}
                                    style={SCALED_CHECKBOX_STYLE}
                                />
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-5" />

            {/* Cancellation Actor Visibility section */}
            <div className="cancellation_actor_settings flex flex-wrap px-3 md:px-0">
                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Cancellation Visibility</h3>
                        <p className="text-sm text-gray-500">Choose whether client can see who performed a cancellation</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4">
                    <div className="w-full flex flex-col">
                        <label htmlFor="show-cancellation-actor-checkbox" className="label font-medium text-gray-900">
                            Display Cancellation Performed By
                        </label>
                        <input
                            id="show-cancellation-actor-checkbox"
                            type="checkbox"
                            data-testid="show-cancellation-actor-checkbox"
                            className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                            checked={show_cancellation_actor ?? false}
                            onChange={e => {
                                setValue('show_cancellation_actor', e.target.checked, { shouldDirty: true });
                            }}
                            style={SCALED_CHECKBOX_STYLE}
                        />
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-5" />

            {/* Time Slot Limitations section */}
            <div className="timeslot_limitations flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Time Slot Limits</h3>
                        <p className="text-sm text-gray-500">Uncheck if limits apply on the timeslots</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4">
                    <div className="w-full flex flex-col">
                        <label className="label font-medium text-gray-900">Enable Time Slot Limitations</label>
                        <label className="text-sm text-gray-700">
                            <input
                                type="checkbox"
                                className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                                checked={!!timeslot_limits}
                                onChange={e => {
                                    handleTimeslotSettingsChange
                                    setValue('timeslot_limits', e.target.checked, { shouldDirty: true });
                                }}
                                style={SCALED_CHECKBOX_STYLE}
                            />
                        </label>
                    </div>
                    {/* Only render TimeslotLimitsTable if timeslot_limits is true */}
                    {Boolean(timeslot_limits) && (
                        <TimeslotLimitsTable
                            selectedCompany={selectedCompany}
                            timeslots={timeslots}
                            user={user}
                            timeSlotRows={timeSlotRows}
                            handleTimeslotSettingsChange={handleTimeslotSettingsChange}
                            allowInboundBookings={allow_inbound_bookings ?? true}
                            allowOutboundBookings={allow_outbound_bookings ?? true}
                        />
                    )}
                </div>
            </div>

            <hr className="border border-gray-200 my-5" />

            {/* Company Addresses section */}
            <div className="company_addresses flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Company Addresses</h3>
                        <p className="text-sm text-gray-500">Addresses associated with the company</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4">
                    <label className="label font-medium text-gray-900">Add New Address</label>
                    <AddressTable 
                        selectedCompany={selectedCompany}
                        companyAddresses={companyAddresses}
                        setIsLoading={setIsLoading}
                        googleMapsApiKey={googleMapsApiKey}
                    />
                </div>
            </div>

            <hr className="border border-gray-200 my-3 mb-16" />

            {/* Save and revert buttons */}
            <div className="relative pt-8">
                <div className="save_revert_buttons absolute bottom-0 right-0 flex space-x-3">
                    <button
                        className={`button p-2 text-white rounded text-sm flex align-center justify-center relative cursor-pointer ${
                            isLoading || !isDirty || isFetchingPermissions ? "bg-gray-400 cursor-not-allowed" : "bg-purple-600 hover:bg-purple-700" // Adjusted class for purple button
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={()=> updateCompany()}
                        disabled={isLoading || !isDirty || isFetchingPermissions} // Disable if loading, no changes, or fetching permissions
                    >
                        {isLoading ? "Saving..." : 'Save'}
                    </button>
                    <button
                        className={`p-2 text-white rounded text-sm flex items-center justify-center relative cursor-pointer ${!isDirty || isFetchingPermissions ? "bg-gray-400 cursor-not-allowed" : "bg-gray-500 hover:bg-gray-600"}`} // Disable revert if no changes or fetching
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={handleRevertChanges}
                        disabled={!isDirty || isFetchingPermissions} // Disable if no changes or fetching
                    >
                        Revert
                    </button>
                </div>
            </div>
        </div>
    );

}

Bookings.propTypes = {
    selectedCompany: PropTypes.shape({
        id: PropTypes.number,
        allow_inbound_bookings: PropTypes.bool,
        allow_outbound_bookings: PropTypes.bool,
        show_cancellation_actor: PropTypes.bool,
        cancellation_range: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        booking_time_range: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        opening_time: PropTypes.string,
        cut_off_time: PropTypes.string,
        timeslot_limits: PropTypes.bool,
        csb_role: PropTypes.string,
        timezone: PropTypes.string,
    }),
    fetchCompany: PropTypes.func,
    companyAddresses: PropTypes.array,
    googleMapsApiKey: PropTypes.string,
    timeslots: PropTypes.array,
    user: PropTypes.object,
};

export default Bookings;